import { motion, AnimatePresence } from 'motion/react';
import { X, Upload, FileSpreadsheet, CheckCircle2 } from 'lucide-react';
import { useRef, useState } from 'react';
import { useAppColors } from '@/src/lib/theme';
import { supabase } from '@/src/lib/supabase';
import { log } from '@/src/lib/logger';
import { formatMoney } from '@/src/lib/format';
import { parseSalesAndTakings, type ParsedSalesDay } from '@/src/lib/ordermateCsv';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  venueId: string;
  userId: string;
  onImported: () => void;
  /** Stacking order, for opening it over another full-screen layer (venue setup). Default 60. */
  zIndex?: number;
}

const fmtDate = (iso: string) => new Date(`${iso}T00:00:00`).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' });

export default function SalesImportModal({ isOpen, onClose, venueId, userId, onImported, zIndex = 60 }: Props) {
  const c = useAppColors();
  const fileRef = useRef<HTMLInputElement>(null);
  const [fileName, setFileName] = useState('');
  const [days, setDays] = useState<ParsedSalesDay[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState<{ imported: number; kept: number } | null>(null);

  const reset = () => { setFileName(''); setDays([]); setError(null); setDone(null); setBusy(false); if (fileRef.current) fileRef.current.value = ''; };
  const close = () => { reset(); onClose(); };

  const onFile = async (f: File | undefined) => {
    if (!f) return;
    reset();
    setFileName(f.name);
    const { days: parsed, error: err } = parseSalesAndTakings(await f.text());
    if (err) { setError(err); return; }
    setDays(parsed.sort((a, b) => a.trade_date.localeCompare(b.trade_date)));
  };

  const doImport = async () => {
    setBusy(true); setError(null);
    // Days that already have a submitted nightly report keep the manager's numbers.
    const { data: existing, error: exErr } = await supabase.from('sales_days').select('trade_date')
      .eq('venue_id', venueId).eq('status', 'submitted')
      .gte('trade_date', days[0].trade_date).lte('trade_date', days[days.length - 1].trade_date);
    if (exErr) {
      setBusy(false);
      setError(/sales_days/.test(exErr.message) ? 'Sales import isn\'t set up yet (the database update hasn\'t been run).' : 'Could not check existing days. Please try again.');
      return;
    }
    const keep = new Set((existing ?? []).map((r) => r.trade_date as string));
    const rows = days.filter((d) => !keep.has(d.trade_date)).map((d) => ({
      ...d, venue_id: venueId, source: 'ordermate_csv', created_by: userId, updated_at: new Date().toISOString(),
    }));
    for (let i = 0; i < rows.length; i += 200) {
      const { error: upErr } = await supabase.from('sales_days').upsert(rows.slice(i, i + 200), { onConflict: 'venue_id,trade_date' });
      if (upErr) { log.warn('sales import failed', { code: upErr.code }); setBusy(false); setError(`Import stopped after ${i} days: ${upErr.message}`); return; }
    }
    setBusy(false);
    setDone({ imported: rows.length, kept: keep.size });
    onImported();
  };

  const total = days.reduce((a, d) => a + (d.total_sales ?? 0), 0);
  const covers = days.reduce((a, d) => a + (d.covers ?? 0), 0);

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={close} className="fixed inset-0 bg-ink/60 backdrop-blur-sm" style={{ zIndex }} />
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.98 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: 20, scale: 0.98 }}
            transition={{ type: 'spring', stiffness: 400, damping: 30 }}
            className="modal-panel fixed inset-x-4 lg:inset-x-auto lg:left-1/2 lg:-translate-x-1/2 lg:w-[520px] rounded-2xl flex flex-col overflow-hidden"
            style={{ background: c.bg, border: `1px solid ${c.border}`, zIndex }}
          >
            <header className="flex items-center justify-between px-6 py-4" style={{ borderBottom: `1px solid ${c.border}` }}>
              <div className="flex items-center gap-3">
                <FileSpreadsheet size={18} style={{ color: c.iconColor }} />
                <h2 className="text-sm font-semibold tracking-wider uppercase" style={{ color: c.text }}>Import OrderMate sales</h2>
              </div>
              <button onClick={close} aria-label="Close" className="p-2 -m-1 rounded-full" style={{ color: c.textDim }}><X size={18} /></button>
            </header>

            <div className="px-6 py-5 space-y-4 overflow-y-auto">
              {done ? (
                <div className="text-center py-6 space-y-2">
                  <CheckCircle2 size={36} className="mx-auto" style={{ color: c.accentSoft }} />
                  <p className="text-sm font-medium" style={{ color: c.text }}>{done.imported} days imported</p>
                  {done.kept > 0 && <p className="text-[13px]" style={{ color: c.textDim }}>{done.kept} days already had a nightly report and were left as they are.</p>}
                  <button onClick={close} className="mt-3 px-4 h-10 rounded-xl text-sm font-semibold" style={{ background: c.accent, color: 'white' }}>Done</button>
                </div>
              ) : (
                <>
                  <ol className="text-[13px] leading-relaxed space-y-1 list-decimal pl-4" style={{ color: c.textDim }}>
                    <li>In the OrderMate portal, open <b style={{ color: c.text }}>Reports → Sales-Takings → Sales And Takings Analysis</b>.</li>
                    <li>Pick the date range (for example the last 12 months) and export as <b style={{ color: c.text }}>CSV</b>.</li>
                    <li>Upload the file here. Importing the same days again just updates them.</li>
                  </ol>

                  <input ref={fileRef} type="file" accept=".csv,text/csv" className="hidden" onChange={(e) => void onFile(e.target.files?.[0])} />
                  <button onClick={() => fileRef.current?.click()}
                    className="w-full h-20 rounded-xl flex flex-col items-center justify-center gap-1.5 text-sm"
                    style={{ background: c.iconBg, border: `1px dashed ${c.borderStrong}`, color: c.textDim }}>
                    <Upload size={18} />
                    {fileName || 'Choose CSV file'}
                  </button>

                  {error && <p className="text-[13px]" style={{ color: c.danger }}>{error}</p>}

                  {days.length > 0 && (
                    <div className="rounded-xl p-4 space-y-1.5 text-sm" style={{ background: c.bgElevated, border: `1px solid ${c.border}` }}>
                      <Row k="Days found" v={String(days.length)} />
                      <Row k="From" v={`${fmtDate(days[0].trade_date)} to ${fmtDate(days[days.length - 1].trade_date)}`} />
                      <Row k="Total sales (inc GST)" v={formatMoney(total)} />
                      <Row k="Covers" v={covers.toLocaleString()} />
                    </div>
                  )}

                  {days.length > 0 && (
                    <button onClick={() => void doImport()} disabled={busy}
                      className="w-full h-11 rounded-xl text-sm font-semibold disabled:opacity-50"
                      style={{ background: c.accent, color: 'white' }}>
                      {busy ? 'Importing…' : `Import ${days.length} days`}
                    </button>
                  )}
                </>
              )}
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}

function Row({ k, v }: { k: string; v: string }) {
  const c = useAppColors();
  return <div className="flex justify-between gap-4"><span style={{ color: c.textDim }}>{k}</span><span className="font-medium text-right" style={{ color: c.text }}>{v}</span></div>;
}
